/**
 * Notification permission + Web Push subscription for the charts page.
 * Asks once on first visit; browsers that only allow the prompt from a user action
 * (Safari, Firefox) get it on the first click / key press instead.
 */

const ASKED_KEY = "bc_push_asked_v1";

function b64urlToBytes(s: string): Uint8Array {
  const pad = "=".repeat((4 - (s.length % 4)) % 4);
  const raw = atob((s + pad).replace(/-/g, "+").replace(/_/g, "/"));
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

function supported(): boolean {
  return "Notification" in window && "serviceWorker" in navigator && "PushManager" in window;
}

export async function syncPushSubscription(): Promise<void> {
  if (!supported() || Notification.permission !== "granted") return;
  try {
    const reg = await navigator.serviceWorker.ready;
    let sub = await reg.pushManager.getSubscription();
    if (!sub) {
      const res = await fetch("/api/push.php?action=key", { credentials: "same-origin" });
      const j = await res.json();
      if (!j || !j.key) return;
      sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: b64urlToBytes(String(j.key)) as BufferSource,
      });
    }
    const r = await fetch("/api/push.php?action=subscribe", {
      method: "POST",
      credentials: "same-origin",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ subscription: sub.toJSON() }),
    });
    if (r.ok) window.dispatchEvent(new CustomEvent("bc-push-ready", { detail: { endpoint: sub.endpoint } }));
  } catch {
    /* push service unavailable (e.g. private window) — on-page alerts still work */
  }
}

/** Endpoint of this browser's push subscription, or null when push is not set up. */
export async function getPushEndpoint(): Promise<string | null> {
  if (!supported() || Notification.permission !== "granted") return null;
  try {
    const reg = await navigator.serviceWorker.getRegistration();
    const sub = reg ? await reg.pushManager.getSubscription() : null;
    return sub ? sub.endpoint : null;
  } catch {
    return null;
  }
}

export function requestNotifications(): Promise<NotificationPermission | "unsupported"> {
  if (!("Notification" in window)) return Promise.resolve("unsupported");
  try {
    localStorage.setItem(ASKED_KEY, "1");
  } catch {
    /* ignore */
  }
  const done = (p: NotificationPermission) => {
    if (p === "granted") syncPushSubscription();
    return p;
  };
  try {
    return Notification.requestPermission().then(done);
  } catch {
    return Promise.resolve(Notification.permission);
  }
}

export function initPush(): void {
  if (!("Notification" in window)) return;
  if (Notification.permission === "granted") {
    syncPushSubscription();
    return;
  }
  if (Notification.permission === "denied") return;
  let asked = false;
  try {
    asked = localStorage.getItem(ASKED_KEY) === "1";
  } catch {
    /* ignore */
  }
  const onFirstAction = function () {
    window.removeEventListener("pointerdown", onFirstAction, true);
    window.removeEventListener("keydown", onFirstAction, true);
    if (Notification.permission === "default") requestNotifications();
  };
  if (!asked) {
    setTimeout(function () {
      requestNotifications().then(function (p) {
        if (p === "default") {
          window.addEventListener("pointerdown", onFirstAction, true);
          window.addEventListener("keydown", onFirstAction, true);
        }
      });
    }, 1500);
  }
}

/** Shows through the service worker when possible (works in background tabs and on Android). */
export function showLocalNotification(title: string, body: string, tag: string): void {
  if (!("Notification" in window) || Notification.permission !== "granted") return;
  const opts: NotificationOptions = { body, tag, requireInteraction: true, icon: "/assets/img/icon-192.png" };
  if ("serviceWorker" in navigator) {
    navigator.serviceWorker.getRegistration().then(
      function (reg) {
        if (reg) {
          reg.showNotification(title, Object.assign({ data: { url: "/charts" } }, opts));
        } else {
          fallback();
        }
      },
      fallback
    );
    return;
  }
  fallback();
  function fallback() {
    try {
      const n = new Notification(title, opts);
      n.onclick = function () {
        window.focus();
        n.close();
      };
    } catch {
      /* ignore */
    }
  }
}
